import { Link } from '@tanstack/react-router'
import { useId } from 'react'
import type {
  StrokesGainedCategory,
  StrokesGainedComponentVersion,
  StrokesGainedIssue,
  StrokesGainedOverview,
  StrokesGainedRound,
} from '../../lib/strokesGainedTypes'
import { BrassRule } from './BrassRule'
import { EmptyPlate } from './EmptyPlate'

const CATEGORIES: readonly { key: StrokesGainedCategory; label: string }[] = [
  { key: 'off_the_tee', label: 'Off the tee' },
  { key: 'approach', label: 'Approach' },
  { key: 'around_the_green', label: 'Around the green' },
  { key: 'putting', label: 'Putting' },
]

const STATUS_GUIDANCE = {
  incomplete: 'Repair the missing observation facts.',
  invalid: 'The observations do not reconcile with the official card.',
  not_tracked: 'This round was not tracked for strokes gained.',
} as const

// The server files a tracked round under the unavailable benchmark as
// `not_tracked` too, and a round with no current analysis the same way. Both
// are "unavailable", never "you did not track this": the headline comes from
// the issue that says so, and only a round with neither issue was untracked.
const UNAVAILABLE_ISSUES = new Set(['benchmark_unavailable', 'calculation_not_current'])

const ISSUE_GUIDANCE: Record<string, string> = {
  benchmark_unavailable: 'the prototype benchmark is not installed; no substitute baseline is used.',
  calculation_not_current: 'no current analysis exists.',
  duplicate_hole_key: 'analysis input is structurally invalid.',
  duplicate_transition_key: 'analysis input is structurally invalid.',
  duplicate_transition_sequence: 'analysis input is structurally invalid.',
  empty_counted_path: 'no counted stroke path was recorded.',
  empty_round: 'no analyzed holes were supplied.',
  incoherent_observation: 'observations conflict with one another.',
  invalid_causal_transition: 'analysis input is structurally invalid.',
  invalid_kernel_input: 'analysis input is structurally invalid.',
  invalid_putt_origin: 'putt origin is invalid.',
  irrecoverable_unknown: 'a required fact was explicitly marked unknown, so analysis is unavailable.',
  missing_distance: 'distance remaining is missing.',
  missing_effective_pin: 'effective pin is missing.',
  missing_observation: 'hole observations are missing.',
  missing_opening_tee: 'opening tee state is missing.',
  missing_official_score_snapshot: 'official score snapshot is missing.',
  missing_putt_origin: 'first-putt origin is missing.',
  missing_strokes: 'recorded strokes are missing.',
  missing_terminal_outcome: 'explicit hole-out is missing.',
  official_score_mismatch: 'observations do not match the official score.',
  hole_score_mismatch: 'analysis charges do not match the official score.',
  round_score_mismatch: 'analysis charges do not match the official score.',
  transition_order: 'analysis input is structurally invalid.',
  unresolved_transition: 'a stroke outcome is unresolved.',
  unterminated_hole: 'explicit hole-out is missing.',
  unqualified_distance: 'distance remaining is not qualified.',
  unqualified_effective_pin: 'effective pin is not qualified.',
  unqualified_putt_distance: 'putt distance is not qualified.',
  unqualified_putt_origin: 'putt origin is not qualified.',
  unqualified_start_lie: 'start lie is not qualified.',
  unsupported_benchmark_state: 'state is outside the reviewed benchmark support.',
  unsupported_hole_par: 'hole par is outside the reviewed analysis support.',
  unsupported_lie: 'lie is outside the reviewed analysis support.',
  unsupported_observation_schema: 'observation format is outside the reviewed analysis support.',
}

function issueGuidance(issue: StrokesGainedIssue): string {
  const place = issue.hole_number === null ? '' : `Hole ${issue.hole_number} · `
  const fact = ISSUE_GUIDANCE[issue.code] ?? 'analysis is unavailable for this round.'
  return `${place}${fact}`
}

function hasCompleteValue(round: StrokesGainedRound | null): round is StrokesGainedRound {
  return round?.status === 'complete' && round.total !== null
}

function formatPlayedOn(playedOn: string) {
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(`${playedOn}T00:00:00Z`),
  )
}

// The em dash a sighted reader takes for "nothing here" is silent at a screen
// reader's default punctuation level, so the same figure carries the word.
function Figure({ display }: { display: string | null | undefined }) {
  if (display != null) return <>{display}</>
  return (
    <>
      <span aria-hidden="true">—</span>
      <span className="sr-only">Unavailable</span>
    </>
  )
}

function benchmarkName(benchmark: StrokesGainedComponentVersion) {
  return benchmark.id === 'pga-tour-shotlink-2025'
    ? '2025 PGA TOUR ShotLink prototype baseline'
    : `${benchmark.id} ${benchmark.version}`
}

function ValueRows({ round, headingLevel = 2 }: { round: StrokesGainedRound | null; headingLevel?: 2 | 3 }) {
  const values = hasCompleteValue(round) ? round.categories : null
  const Heading = headingLevel === 2 ? 'h2' : 'h3'
  return (
    <div>
      <Heading className="font-display text-[25px] font-medium text-pine">Where the round moved</Heading>
      <dl className="mt-2">
        {CATEGORIES.map(({ key, label }) => (
          <div
            key={key}
            className="grid min-h-[55px] grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 border-b border-brass/25 py-3 sm:grid-cols-[minmax(112px,148px)_minmax(0,1fr)_52px] sm:items-center sm:py-0"
          >
            <dt className="text-[13px] font-semibold text-ink">{label}</dt>
            <dd aria-hidden="true" className="order-3 col-span-2 relative h-2.5 sm:order-none sm:col-span-1">
              <span className="absolute inset-x-0 top-1 border-t border-ink-soft/40" />
              <span className="absolute bottom-0 left-1/2 top-0 border-l border-brass" />
            </dd>
            <dd className="text-right font-display text-[16px] font-semibold tabular-nums text-pine">
              <Figure display={values?.[key].display} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function roundHeadline(status: Exclude<StrokesGainedRound['status'], 'complete'>, issues: StrokesGainedIssue[]) {
  if (status === 'not_tracked' && issues.some((issue) => UNAVAILABLE_ISSUES.has(issue.code))) {
    return 'Strokes gained unavailable.'
  }
  return STATUS_GUIDANCE[status]
}

function RoundState({ round }: { round: StrokesGainedRound }) {
  if (round.status === 'complete') return null
  return (
    <div className="mt-4 border-l-2 border-brass-deep bg-parchment px-4 py-3 text-[12.5px] text-ink-soft">
      <p className="font-semibold text-pine">{roundHeadline(round.status, round.issues)}</p>
      {round.issues.map((issue, index) => (
        <p key={`${issue.code}-${issue.hole_number ?? 'round'}-${index}`} className="mt-1">
          {issueGuidance(issue)}
        </p>
      ))}
    </div>
  )
}

function TotalPanel({ overview }: { overview: StrokesGainedOverview }) {
  const latest = overview.latest_round
  const unavailable = overview.benchmark.version === 'unavailable'
  const availableLatest = hasCompleteValue(latest)
  return (
    <div className="border-b border-brass pb-7 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-10">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-brass-deep">
        {unavailable ? 'Current state' : 'Latest round'}
      </p>
      <p className="mt-2 font-display text-[clamp(4rem,8vw,6.5rem)] font-semibold leading-[0.9] tabular-nums text-pine">
        <Figure display={availableLatest ? latest.total?.display : null} />
      </p>
      <p className="mt-3 text-[13px] text-ink-soft">
        {unavailable
          ? 'Benchmark unavailable. No round or trend value is estimated.'
          : availableLatest
            ? `${latest.holes} holes · actual, unscaled`
            : latest
              ? `${latest.holes} holes · unavailable`
              : 'No tracked rounds yet.'}
      </p>
      <div className="mt-6 flex flex-wrap items-baseline justify-between gap-3 bg-pine px-4 py-3 text-paper">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sage">
          Last five · SG / 18
        </span>
        <strong className="font-display text-[23px] font-semibold tabular-nums">
          <Figure display={overview.trend.total?.display} />
        </strong>
      </div>
      <p className="mt-2 text-[11.5px] text-ink-soft">
        {overview.trend.complete_rounds} complete · {overview.trend.holes_analyzed} holes analyzed
      </p>
      {latest && <RoundState round={latest} />}
    </div>
  )
}

function RecentRounds({ overview }: { overview: StrokesGainedOverview }) {
  const unavailable = overview.benchmark.version === 'unavailable'
  const selected = overview.coverage.selected_rounds
  const coverage = unavailable
    ? `${selected === 5 ? 'Five' : selected} selected · benchmark unavailable`
    : `${selected} selected · ${overview.coverage.complete_rounds} complete`
  return (
    <section aria-labelledby="sg-recent-heading" className="bg-paper-deep py-10">
      <div className="mx-auto w-full max-w-[1120px] px-4 sm:px-10">
        <div className="flex flex-wrap items-baseline gap-4">
          <h2 id="sg-recent-heading" className="font-display text-[29px] font-medium text-pine">Recent rounds</h2>
          <BrassRule className="min-w-12 flex-1" />
          <span className="text-[12px] text-ink-soft">{coverage}</span>
        </div>
        <div className="mt-5 border-t border-brass bg-parchment">
          {overview.recent_rounds.map((round) => (
            <article
              key={round.round_id}
              data-sg-recent-round={round.round_id}
              className="grid grid-cols-1 items-center gap-2 border-b border-brass/25 px-3 py-4 sm:grid-cols-[minmax(72px,110px)_minmax(0,1fr)_auto] sm:gap-3 sm:px-5"
            >
              <time className="text-[11px] text-ink-soft" dateTime={round.played_on}>
                {formatPlayedOn(round.played_on)}
              </time>
              <div>
                {/* Five rounds at the home course are five links with one name
                    unless the date is in it (WCAG 2.4.4); the visible course
                    name stays in the name (2.5.3). */}
                <Link to={`/clubhouse/rounds/${round.round_id}`} aria-label={`Open round at ${round.course.name}, ${formatPlayedOn(round.played_on)}`} className="rounded-club-sm font-display font-semibold text-pine underline decoration-brass underline-offset-4">
                  {round.course.name}
                </Link>
                <p className="mt-0.5 text-[11px] text-ink-soft">{round.holes} holes · {hasCompleteValue(round) ? 'actual, unscaled' : 'unavailable'}</p>
              </div>
              <strong className="text-right font-display text-[18px] font-semibold tabular-nums text-pine">{hasCompleteValue(round) ? round.total?.display : 'Unavailable'}</strong>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export function StrokesGainedOverviewView({ overview }: { overview: StrokesGainedOverview }) {
  if (overview.latest_round === null) {
    return (
      <section aria-label="Private strokes gained" className="mx-auto w-full max-w-[1120px] px-4 py-10 sm:px-10">
        <EmptyPlate headingLevel={2} title="No tracked rounds yet." description="Your private ledger begins when a completed nine- or eighteen-hole round carries its own observations." />
      </section>
    )
  }
  return (
    <section aria-label="Private strokes gained">
      <div className="mx-auto w-full max-w-[1120px] px-4 pb-9 pt-10 sm:px-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brass-deep">{benchmarkName(overview.benchmark)}</p>
        {overview.benchmark.version === 'unavailable' && (
          <p className="mt-2 max-w-[65ch] text-[13px] text-ink-soft">The prototype benchmark is not installed. Saved observations remain private and no substitute baseline is used.</p>
        )}
        <div className="grid gap-8 pt-7 lg:grid-cols-[minmax(260px,0.8fr)_minmax(440px,1.2fr)] lg:gap-12">
          <TotalPanel overview={overview} />
          <ValueRows round={overview.latest_round} />
        </div>
      </div>
      <RecentRounds overview={overview} />
    </section>
  )
}

export function StrokesGainedRoundView({ result }: { result: StrokesGainedRound }) {
  const titleId = useId()
  const available = hasCompleteValue(result)
  return (
    <section aria-labelledby={titleId}>
      <div className="flex flex-wrap items-baseline gap-4">
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-brass-deep">Private</p>
          <h2 id={titleId} className="font-display text-[24px] font-medium text-pine">Strokes gained.</h2>
        </div>
        <BrassRule className="min-w-12 flex-1" />
        <span className="text-[12px] text-ink-soft">Visible only to you</span>
      </div>
      <div className="mt-5 grid gap-6 rounded-club-lg border border-brass bg-parchment px-5 py-5 lg:grid-cols-[180px_1fr]">
        <div>
          <p className="font-display text-[48px] font-semibold leading-none tabular-nums text-pine"><Figure display={available ? result.total?.display : null} /></p>
          <p className="mt-2 text-[12px] text-ink-soft">{result.holes} holes · {available ? 'actual, unscaled' : 'unavailable'}</p>
          <RoundState round={result} />
        </div>
        <ValueRows round={result} headingLevel={3} />
      </div>
    </section>
  )
}
